import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Paginator } from "@/components/ui/Paginator";
import { ProgrammeModel } from "@/models/programme.model";
import { programmesService } from "@/services/programmesService";
import { PaginationMeta } from "@/models/templates/pagination.model";
import { programmeImages } from "@/utils/programmes";
import { cn } from "@/utils/cn";
import { CheckCircle, Loader2, Search } from "lucide-react";

type ProgrammeSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (programme: ProgrammeModel) => void;
  selectedProgrammeId?: string;
  initialProgrammes?: ProgrammeModel[];
  isInitialLoading?: boolean;
};

export function ProgrammeSelectModal({
  isOpen,
  onClose,
  onSelect,
  selectedProgrammeId,
  initialProgrammes,
  isInitialLoading,
}: ProgrammeSelectModalProps) {
  const [programmePage, setProgrammePage] = useState(1);
  const [programmePerPage, setProgrammePerPage] = useState(10);
  const [programmeMeta, setProgrammeMeta] = useState<PaginationMeta | undefined>();
  const [programmeResults, setProgrammeResults] = useState<ProgrammeModel[]>([]);
  const [programmeSearch, setProgrammeSearch] = useState("");
  const [programmeError, setProgrammeError] = useState<string | null>(null);
  const [isProgrammeFetching, setIsProgrammeFetching] = useState(false);

  const fetchProgrammesPage = useCallback(
    async (page = programmePage, perPage = programmePerPage) => {
      setIsProgrammeFetching(true);
      setProgrammeError(null);
      try {
        const response = await programmesService.getProgrammesPage(
          { page, perPage },
          { requireAuth: false, skipCSRF: true },
        );
        setProgrammeResults(response.data);
        setProgrammeMeta(response.meta);
      } catch (err: any) {
        setProgrammeError(
          err?.message || "Unable to load programmes right now.",
        );
      } finally {
        setIsProgrammeFetching(false);
      }
    },
    [programmePage, programmePerPage],
  );

  useEffect(() => {
    if (!isOpen) return;
    void fetchProgrammesPage(programmePage, programmePerPage);
  }, [fetchProgrammesPage, isOpen, programmePage, programmePerPage]);

  const availableProgrammes = useMemo(
    () =>
      programmeResults.length > 0
        ? programmeResults
        : initialProgrammes ?? [],
    [programmeResults, initialProgrammes],
  );

  const filteredProgrammes = useMemo(
    () =>
      availableProgrammes.filter((programme) =>
        programme.title
          .toLowerCase()
          .includes((programmeSearch || "").toLowerCase()),
      ),
    [availableProgrammes, programmeSearch],
  );

  const handleProgrammePageChange = (page: number) => {
    setProgrammePage(Math.max(1, page));
  };

  const handleProgrammePerPageChange = (limit: number) => {
    setProgrammePerPage(limit);
    setProgrammePage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select a programme"
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 z-10" />
            <Input
              value={programmeSearch}
              onChange={(event) => setProgrammeSearch(event.target.value)}
              placeholder="Search programmes"
              className="w-full border-slate-200 pl-9"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => fetchProgrammesPage(1, programmePerPage)}
            disabled={isProgrammeFetching}
            className="w-full justify-center md:w-auto"
          >
            {isProgrammeFetching ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing
              </span>
            ) : (
              "Refresh list"
            )}
          </Button>
        </div>

        {programmeError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {programmeError}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2">
          {(isInitialLoading || isProgrammeFetching) && filteredProgrammes.length === 0 ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-[320px] rounded-[32px] border border-slate-200 bg-slate-100 animate-pulse"
              />
            ))
          ) : filteredProgrammes.length === 0 ? (
            <div className="col-span-full rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
              No programmes found. Adjust your search or refresh the list.
            </div>
          ) : (
            filteredProgrammes.map((programme, index) => (
              <button
                key={programme.id ?? `${programme.title}-${index}`}
                type="button"
                onClick={() => onSelect(programme)}
                className={cn(
                  "group relative flex h-full max-w-sm flex-col overflow-hidden rounded-[32px] bg-white text-left shadow-xl transition-all hover:-translate-y-1",
                  selectedProgrammeId === programme.id
                    ? "ring-2 ring-[#7B2A40]"
                    : "",
                )}
              >
                <div className="relative h-auto min-h-[220px]">
                  <img
                    src={programmeImages[index % programmeImages.length]}
                    alt={programme.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent transition duration-300 group-hover:from-black/70" />

                  <div className="absolute top-0 w-full px-8 pt-4 pb-4 text-left text-white">
                    <div className="mb-2 flex w-full flex-wrap items-start justify-between gap-3 xl:flex-nowrap">
                      <h3 className="text-lg font-semibold">{programme.title}</h3>
                      {programme.priceLabel && (
                        <div className="min-w-32 rounded-full bg-[#7b2a40] px-4 py-1 text-sm font-semibold text-white text-center shadow-lg text-nowrap">
                          {programme.priceLabel}
                        </div>
                      )}
                    </div>
                    {programme.type && (
                      <p className="mt-1 text-sm uppercase tracking-wide text-white/70">
                        {programme.type.replace(/_/g, " ")}
                      </p>
                    )}

                    <div className="mt-4 flex items-center gap-2 text-sm text-white/80">
                      <span>
                        {programme.cohorts?.length ?? 0} cohort
                        {(programme.cohorts?.length ?? 0) === 1 ? "" : "s"} available
                      </span>
                    </div>
                    <Button
                      type="button"
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#ffffff] px-6 text-primary transition hover:bg-[#63182d] hover:text-white"
                    >
                      {selectedProgrammeId === programme.id ? (
                        <>
                          Selected <CheckCircle className="h-4 w-4" />
                        </>
                      ) : (
                        "Choose"
                      )}
                    </Button>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <Paginator
          meta={programmeMeta}
          page={programmePage}
          perPage={programmePerPage}
          onPageChange={handleProgrammePageChange}
          onPerPageChange={handleProgrammePerPageChange}
          isPreviousData={isProgrammeFetching}
        />
      </div>
    </Modal>
  );
}
